import { useState, type CSSProperties, type ReactNode } from 'react';
import { cleanAsk, FRONT_DOOR_TEXT_LIMIT, type FrontDoorAnswer, type FrontDoorChoice } from '../../core/frontDoor';
import { LISTING_ICONS } from './layoutListings';
import './frontDoor.css';

const TILT = [-1.2, 1, 0.8, -1];
const TINTS = ['mint', 'sky', 'lilac', 'rose'];

export interface FrontDoorProps {
  choices: FrontDoorChoice[];
  /** Where "I'm not teaching yet" goes: back to the study app. */
  studyHref: string;
  onAnswer: (answer: FrontDoorAnswer, details?: { tool?: string; text?: string }) => void;
  /** Whatever the layout's own hero kept beside its headline, such as Lab Bench's updates card. */
  aside?: ReactNode;
}

/**
 * E13 `question` arm. The question is the page's headline: it replaces the layout's intro.
 * Styled by whichever layout it sits in; see frontDoor.css.
 */
export default function FrontDoor({ choices, studyHref, onAnswer, aside }: FrontDoorProps) {
  const [text, setText] = useState('');
  const [sent, setSent] = useState(false);

  return (
    <section className="fd" aria-labelledby="fd-heading">
      <div className="fd-head">
        <p className="fd-kicker">240 Labs · free tools for teachers</p>
        <h1 id="fd-heading">What are you trying to <span className="fd-mark">figure out?<svg viewBox="0 0 300 30" preserveAspectRatio="none" aria-hidden="true"><path d="M4 22 C 60 6, 120 28, 180 14 S 270 8, 296 18" /></svg></span></h1>
        <p className="fd-lede">Pick the question closest to yours. We’ll open the tool that answers it.</p>
      </div>
      <div className="fd-panel">
        <p className="fd-tag" aria-hidden="true">pick one</p>
        <ul className="fd-answers">
          {choices.map((choice, i) => (
            <li key={choice.answer}>
              <a className={`fd-answer fd-tint-${TINTS[i % TINTS.length]}`} href={choice.href}
                style={{ '--fd-r': `${TILT[i % TILT.length]}deg` } as CSSProperties}
                onClick={() => onAnswer(choice.answer, { tool: choice.tool })}>
                <span className="fd-glyph" aria-hidden="true">{LISTING_ICONS[choice.tool] ?? '✎'}</span>
                <strong>“{choice.question}”</strong>
                <small>{choice.toolName}</small>
              </a>
            </li>
          ))}
        </ul>
        {sent ? (
          <p className="fd-thanks" role="status">Thanks. We read every question that comes in.</p>
        ) : (
          <form className="fd-ask" onSubmit={event => {
            event.preventDefault();
            const asked = cleanAsk(text);
            if (!asked) return;
            onAnswer('other', { text: asked });
            setSent(true);
          }}>
            <label className="fd-hidden-label" htmlFor="fd-ask-input">Ask a different question</label>
            <input id="fd-ask-input" type="text" value={text} maxLength={FRONT_DOOR_TEXT_LIMIT}
              placeholder="Got a different question? Ask it here." onChange={event => setText(event.target.value)} />
            <button type="submit">Send</button>
          </form>
        )}
        <a className="fd-not-teaching" href={studyHref} onClick={() => onAnswer('not_teaching')}>I’m not teaching yet →</a>
      </div>
      {/* After the questions in the DOM, so a phone reaches them first; the grid lifts it beside the headline on wide screens. */}
      {aside && <div className="fd-aside">{aside}</div>}
    </section>
  );
}
